import joi from 'joi';
import {
  AdvancedFormatComponentAttributes,
  AdvancedFormatComponentI18nAttributes,
  AdvancedFormatOpenType,
  CardLayouts,
} from '@loyalty/editor-components';
import { FormGroup } from '@mui/material';
import { gameTypeNames } from '@loyalty/shared-common/premium-games-api-client';
import { resolveAdvancedFormatConfigId } from '@loyalty/shared-common';
import { useFormContext } from 'react-hook-form';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { InputTextField } from '../../../../shared/forms/fields/InputTextField';
import { PremiumGameField } from './PremiumGameField';
import { SelectField } from '../../../../shared/forms/fields/SelectField';
import { Trait } from '../utils/Trait';
import { TraitFormValue } from '../types';
import { getCardLayoutTemplateSelectOptions } from '../utils/card-layout-template-options';

export const advancedFormatWidgetTraitsSchema = joi.object<TraitFormValue>({
  attributes: joi.object<AdvancedFormatComponentAttributes>({
    configId: joi.string().uuid().optional().empty('').allow(null),
    // widgets configured before the game picker existed still hold the game uuid in a url
    configUrl: joi.string().uri().optional().empty('').allow(null),
    gameType: joi
      .string()
      .valid(...gameTypeNames)
      .optional()
      .empty('')
      .allow(null),
    openType: joi
      .string()
      .valid(AdvancedFormatOpenType.iframe, AdvancedFormatOpenType.newTab)
      .optional()
      .allow(null),
    template: joi
      .string()
      .valid(CardLayouts.DEFAULT, CardLayouts.LEGACY, CardLayouts.BUTTON_ONLY)
      .optional()
      .allow(null, ''),
  }),
  i18nAttributes: joi.object<AdvancedFormatComponentI18nAttributes>({
    title: joi.string().optional().empty('').allow(null),
    image: joi.string().uri().optional().empty('').allow(null),
    description: joi.string().optional().empty('').allow(null),
    ctaLabel: joi.string().optional().empty('').allow(null),
  }),
});

type FormValue = TraitFormValue<
  AdvancedFormatComponentAttributes,
  AdvancedFormatComponentI18nAttributes
>;

const openTypeOptions = (t: (k: string) => string) => [
  {
    id: AdvancedFormatOpenType.iframe,
    name: t('editor.advanced-format.traits.open-type.options.iframe'),
  },
  {
    id: AdvancedFormatOpenType.newTab,
    name: t('editor.advanced-format.traits.open-type.options.new-tab'),
  },
];

export function AdvancedFormatWidgetTraits() {
  const { t } = useTranslation();

  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<FormValue>();
  const attributes = watch('attributes');
  const openType = attributes?.openType ?? AdvancedFormatOpenType.iframe;
  const configId = resolveAdvancedFormatConfigId(attributes);

  const cardLayoutTemplateOptions = useMemo(() => getCardLayoutTemplateSelectOptions(t), [t]);

  const selectGame = (uuid?: string) => {
    setValue('attributes.configId', uuid, { shouldDirty: true, shouldValidate: true });
    if (attributes?.configUrl) {
      // the game uuid now lives in configId, the legacy url must not win over it anymore
      setValue('attributes.configUrl', undefined, { shouldDirty: true, shouldValidate: true });
    }
  };

  const gameTypeOptions = useMemo(
    () =>
      gameTypeNames.map(gameType => ({
        id: gameType,
        name: t(`editor.advanced-format.traits.game-name.options.${gameType}`),
      })),
    [t],
  );

  return (
    <FormGroup>
      <Trait>
        <SelectField
          control={control}
          name="attributes.gameType"
          label={t('editor.advanced-format.traits.game-name.label')}
          options={gameTypeOptions}
        />
      </Trait>
      <Trait>
        <PremiumGameField
          label={t('editor.advanced-format.traits.game.label')}
          gameType={attributes?.gameType}
          value={configId}
          onChange={selectGame}
          errorMessage={errors.attributes?.configId?.message}
        />
      </Trait>
      <Trait>
        <SelectField
          control={control}
          name="attributes.openType"
          label={t('editor.advanced-format.traits.open-type.label')}
          defaultValue={AdvancedFormatOpenType.iframe}
          options={openTypeOptions(t)}
        />
      </Trait>
      {openType === AdvancedFormatOpenType.newTab && (
        <>
          <Trait>
            <SelectField
              control={control}
              name="attributes.template"
              label={t('editor.card-layout.label')}
              defaultValue={CardLayouts.DEFAULT}
              options={cardLayoutTemplateOptions}
            />
          </Trait>
          <Trait>
            <InputTextField<FormValue>
              name="i18nAttributes.title"
              label={t('editor.advanced-format.traits.card-title.label')}
            />
          </Trait>
          <Trait>
            <InputTextField<FormValue>
              name="i18nAttributes.image"
              label={t('editor.advanced-format.traits.card-image.label')}
              placeholder="https://"
            />
          </Trait>
          <Trait>
            <InputTextField<FormValue>
              name="i18nAttributes.description"
              multiline={true}
              rows={3}
              label={t('editor.advanced-format.traits.card-description.label')}
            />
          </Trait>
          <Trait>
            <InputTextField<FormValue>
              name="i18nAttributes.ctaLabel"
              label={t('editor.advanced-format.traits.card-cta.label')}
            />
          </Trait>
        </>
      )}
    </FormGroup>
  );
}
